import { Ref } from 'react';

import { Field, FieldProps } from 'react-final-form';

import { ControlLabel, ControlLabelProps } from '@cast/design-system';

import { useControlsGroupState } from './controls-state';
import { useRffFormState } from '../hooks';
import { resolveRenderableError, showErrorOnChange } from '../utils';

export type RffControlLabelProps = Omit<ControlLabelProps, 'error'> & {
  ref?: Ref<HTMLDivElement>;
  /** Name of the form field whose validation error renders under the label. */
  name: string;
  fieldProps?: Partial<FieldProps<string, any>>;
};

/**
 * Binds the design-system `ControlLabel` to a react-final-form field: the
 * field's validation error (gated by `showErrorOnChange`) is passed as
 * `error`, and `disabled` is resolved from the form mode and the nearest
 * `RffControlsProvider` state.
 */
export const RffControlLabel = ({
  ref,
  fieldProps = {},
  name: _name,
  ...labelProps
}: RffControlLabelProps) => {
  const { mode } = useRffFormState();
  const { disabled, readOnly, name } = useControlsGroupState({
    disabled: labelProps.disabled,
    readOnly: mode === 'view',
    name: _name,
  });

  return (
    <Field
      {...fieldProps}
      name={name || _name}
      render={({ meta }) => {
        const showError = showErrorOnChange(meta);
        return (
          <ControlLabel
            ref={ref}
            {...labelProps}
            error={
              showError
                ? resolveRenderableError(meta.error) ??
                  resolveRenderableError(meta.submitError)
                : undefined
            }
            disabled={disabled || readOnly}
          />
        );
      }}
    />
  );
};
